import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CustomerExportParams,
  CustomerListParams,
  CustomerUpdatePayload,
} from '../../../types/customer'
import type { CustomerAdapter } from '../adapters/types'
import { customerTimelineQueryKey } from './useCustomerTimeline'

// Query-key conventions (mirrors Brand/Tag/User):
//   ['customers', storeType, storeId, params?]
//   ['customer',  storeType, storeId, customerId]
//
// The adapter is the source of truth for `storeType` and `storeId`, so we
// embed both in the key — that way two pantry stores of different types
// never share a cache slot, and switching stores invalidates implicitly.

export const customerListQueryKey = (
  storeType: string,
  storeId: string,
  params?: CustomerListParams,
) => ['customers', storeType, storeId, params] as const

export const customerDetailQueryKey = (
  storeType: string,
  storeId: string,
  customerId: string,
) => ['customer', storeType, storeId, customerId] as const

// ── Queries ──────────────────────────────────────────────────────────────────

export const useCustomerListQuery = (
  adapter: CustomerAdapter,
  params: CustomerListParams,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: customerListQueryKey(adapter.storeType, adapter.storeId, params),
    queryFn: () => adapter.listCustomers(params),
    enabled: (options?.enabled ?? true) && Boolean(adapter.storeId),
    placeholderData: (prev) => prev,
  })

export const useCustomerDetailQuery = (
  adapter: CustomerAdapter,
  customerId: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: customerDetailQueryKey(
      adapter.storeType,
      adapter.storeId,
      customerId ?? '',
    ),
    queryFn: () => adapter.getCustomer(customerId as string),
    enabled:
      (options?.enabled ?? true) &&
      Boolean(adapter.storeId) &&
      Boolean(customerId),
  })

// ── Mutations ────────────────────────────────────────────────────────────────

export const useUpdateCustomer = (adapter: CustomerAdapter) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      customerId,
      payload,
    }: {
      customerId: string
      payload: CustomerUpdatePayload
    }) => adapter.updateCustomer(customerId, payload),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['customers', adapter.storeType, adapter.storeId],
      })
      queryClient.invalidateQueries({
        queryKey: customerDetailQueryKey(
          adapter.storeType,
          adapter.storeId,
          vars.customerId,
        ),
      })
    },
  })
}

export const useDeleteCustomer = (adapter: CustomerAdapter) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (customerId: string) => {
      if (!adapter.deleteCustomer) {
        return Promise.reject(
          new Error(
            `CustomerAdapter:${adapter.storeType}: deleteCustomer is not supported`,
          ),
        )
      }
      return adapter.deleteCustomer(customerId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customers', adapter.storeType, adapter.storeId],
      })
    },
  })
}

// `customerId` here is the POS customer id, used only to key the timeline
// invalidation below. The mutation variable passed to `.mutate()` is the
// customer's `wooCustomerId` — what the adapter's `resetPassword` actually
// addresses (see `CustomerAdapter.resetPassword` for the contract).
export const useResetCustomerPassword = (
  adapter: CustomerAdapter,
  customerId: string,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (wooCustomerId: string) => {
      if (!adapter.resetPassword) {
        return Promise.reject(
          new Error(
            `CustomerAdapter:${adapter.storeType}: resetPassword is not supported`,
          ),
        )
      }
      return adapter.resetPassword(wooCustomerId)
    },
    onSuccess: () => {
      // Refetch the timeline so a server-logged RESET_PASSWORD entry (if the
      // backend writes one for this call) shows up without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: customerTimelineQueryKey(
          adapter.storeType,
          adapter.storeId,
          customerId,
        ),
      })
    },
  })
}

export const useExportCustomers = (adapter: CustomerAdapter) =>
  useMutation({
    mutationFn: (params: CustomerExportParams) => adapter.exportCsv(params),
  })
